import { useParams } from "react-router-dom";
import React, { useEffect, useRef, useMemo, useState } from "react";
import styles from "./PhysicianReportPage.module.css";
import previewStyles from "../Patients/PreviewModal.module.css";
import NewReportPage from "./NewReportPage";
import NewTransmissionsPage from "./NewTransmissionPage";
import NewNotesPage from "./NewNotesPage";
import NewReportGraph from "./NewReportGraph";
import {
  useFetchPdiGraphPage,
  useFetchPegGraphPage,
  useFetchPhqGraphPage,
  useFetchAnxietyGraphPage,
  useFetchBillingCyclePage,
  useFetchRiskRatingPage,
  useFetchAffectPage,
  useFetchSideEffectsPage,
  useImpactOfLivingReportPage,
  useFetchOpioidReportPage,
  useFetchTransmissionPdfPage,
  useFetchPatientNotesPage,
} from "../../hooks/PhysicianReportPage.ts";

function chunkArray<T>(arr: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

interface Props {
  notes?: {
    id: string;
    date: string;
    type: "Clinical" | "Non-Clinical";
    content: string;
    author: string;
    isImportant?: "yes" | "no";
  }[];
}

const PhysicianReportPage: React.FC<Props> = ({ notes: propNotes = [] }) => {
  const transformedPropNotes = React.useMemo(() => {
    return propNotes.map((note) => ({
      _id: note.id,
      noteCategory: note.type.toLowerCase() as "clinical" | "non-clinical",
      richText: note.content,
      createdTimestamp: new Date(note.date).getTime(),
      isImportant: note.isImportant,
    }));
  }, [propNotes]);

  const { billingCycleId, patientId } = useParams<{
    billingCycleId: string;
    patientId: string;
  }>();

  const { pegGraph, getPegGraphData, pegGraphLoading } = useFetchPegGraphPage(
    billingCycleId || ""
  );
  const { pdiGraph, getPdiGraphData, pdiGraphLoading } = useFetchPdiGraphPage(
    billingCycleId || ""
  );
  const { phqGraph, getPhqGraphData, phqGraphLoading } = useFetchPhqGraphPage(
    billingCycleId || ""
  );
  const { anxietyGraph, getAnxietyGraphData, anxietyGraphLoading } =
    useFetchAnxietyGraphPage(billingCycleId || "");

  const { billingCycleData, getBillingCycleData, billingCycleLoading } =
    useFetchBillingCyclePage(billingCycleId || "");

  const { riskRatingData, riskRatingLoading } = useFetchRiskRatingPage(
    billingCycleId || ""
  );

  const { affectData, isLoading: isAffectDataLoading } = useFetchAffectPage(
    billingCycleId || ""
  );

  const {
    impactData: impactOfLivingData,
    isLoading: isImpactDataLoading,
    refetch: refetchImpact,
  } = useImpactOfLivingReportPage(billingCycleId || "");

  const { sideEffects, isLoading: isSideEffectsLoading } =
    useFetchSideEffectsPage(billingCycleId || "");

  const { opioidData, isLoading: isOpioidDataLoading } =
    useFetchOpioidReportPage(billingCycleId || "");

  const {
    notes,
    loading: notesLoading,
    patientBillingMinuteMessage,
  } = useFetchPatientNotesPage(billingCycleId);

  const sortedNotes = useMemo(() => {
    if (!notes) return [];
    return [...notes].sort((a, b) => {
      const order = {
        clinical: 1,
        "non-clinical": 2,
        "timer-summary": 3,
      };

      // sort by priority
      if (order[a.noteCategory] !== order[b.noteCategory]) {
        return order[a.noteCategory] - order[b.noteCategory];
      }
      // If same category -> sort by date
      return a.createdTimestamp - b.createdTimestamp;
    });
  }, [notes]);

  const chunkedNotes = useMemo(() => {
    return chunkArray(sortedNotes, 3);
  }, [sortedNotes]);

  const reportRef = useRef<HTMLDivElement>(null);
  const {
    transmissionData,
    fetchTransmissionData,
    isLoading: transmissionLoading,
    error: transmissionError,
  } = useFetchTransmissionPdfPage(billingCycleId || "");

  const allDataLoaded = !(
    billingCycleLoading ||
    transmissionLoading ||
    notesLoading ||
    riskRatingLoading ||
    isAffectDataLoading ||
    isImpactDataLoading ||
    isSideEffectsLoading ||
    isOpioidDataLoading ||
    pegGraphLoading ||
    pdiGraphLoading ||
    phqGraphLoading ||
    anxietyGraphLoading
  );
  const chunkedQuestions = useMemo(() => {
    if (!transmissionData?.data) return [];

    const firstPageQuestions = transmissionData.data.slice(0, 11);
    const remainingQuestions = transmissionData.data.slice(11);
    const remainingChunks = chunkArray(remainingQuestions, 10);

    return [firstPageQuestions, ...remainingChunks];
  }, [transmissionData?.data]);

  // Billing cycle specific data (only when cycle changes)
  useEffect(() => {
    getPegGraphData();
    getPdiGraphData();
    getPhqGraphData();
    getAnxietyGraphData();
    getBillingCycleData();
    fetchTransmissionData();
  }, [billingCycleId]);

  const graphs = [
    {
      title: "PEG Score",
      data: pegGraph?.data,
      loading: pegGraphLoading,
      color: "rgb(75, 192, 192)",
    },
    {
      title: "PDI Score",
      data: pdiGraph?.data,
      loading: pdiGraphLoading,
      color: "rgb(0, 148, 194)",
    },
    {
      title: "PHQ-2 Score",
      data: phqGraph?.data,
      loading: phqGraphLoading,
      color: "rgb(1, 194, 255)",
    },
    {
      title: "Anxiety Score",
      data: anxietyGraph?.data,
      loading: anxietyGraphLoading,
      color: "rgb(15, 64, 122)",
    },
  ];

  return (
    <>
      <div className={styles.pageContainer}>
        {/* PREVIEW CONTENT */}
        <div className={styles.pageContent}>
          <NewReportPage
            patientId={patientId || ""}
            billingCycleData={billingCycleData}
            sideEffectQuestions={[]}
            billingCycleDataLoading={billingCycleLoading}
            sideEffectQuestionsLoading={false}
            isReadyForDownload={true}
            customHeight="11in"
            customWidth="100%"
            billingCycleId={billingCycleId}
            refetchImpact={refetchImpact}
            impactOfLivingData={impactOfLivingData}
            isImpactDataLoading={isImpactDataLoading}
            isAffectDataLoading={isAffectDataLoading}
            affectData={affectData}
            isSideEffectsLoading={isSideEffectsLoading}
            sideEffects={sideEffects}
            isOpioidDataLoading={isOpioidDataLoading}
            opioidData={opioidData}
            riskRatingLoading={riskRatingLoading}
            riskRatingData={riskRatingData}
          />

          <NewReportGraph
            billingCycleData={billingCycleData}
            sideEffectQuestions={[]}
            graphs={graphs}
            customHeight="11in"
            customWidth="100%"
          />
          {chunkedQuestions.map((chunk, idx) => {
            const startingIndex = idx === 0 ? 0 : 11 + (idx - 1) * 10;
            return (
              <div
                key={idx}
                style={{ pageBreakAfter: "always", marginBottom: "20px" }}
              >
                <NewTransmissionsPage
                  patientId={patientId}
                  customHeight="11in"
                  customWidth="100%"
                  transmissionData={{ data: chunk }}
                  isLoading={transmissionLoading}
                  error={transmissionError}
                  startingIndex={startingIndex}
                />
              </div>
            );
          })}
          {sortedNotes.length && (
            <div style={{ marginBottom: "20px" }}>
              <NewNotesPage
                notes={sortedNotes}
                loading={notesLoading}
                customHeight="11in"
                isPdf={false}
                patientBillingMinuteMessage={patientBillingMinuteMessage}
              />
            </div>
          )}
        </div>
      </div>

      <div
        className={styles.pdfGenerator}
        style={{
          position: "absolute",
          left: "-9999px",
          overflow: "hidden",
          visibility: "hidden",
          display: "none",
        }}
      >
        <div ref={reportRef}>
          <NewReportPage
            patientId={patientId || ""}
            billingCycleData={billingCycleData}
            sideEffectQuestions={[]}
            billingCycleDataLoading={billingCycleLoading}
            sideEffectQuestionsLoading={false}
            isReadyForDownload={allDataLoaded}
            customHeight="9.7in"
            customWidth="8in"
            billingCycleId={billingCycleId}
            refetchImpact={refetchImpact}
            impactOfLivingData={impactOfLivingData}
            isImpactDataLoading={isImpactDataLoading}
            isAffectDataLoading={isAffectDataLoading}
            affectData={affectData}
            isSideEffectsLoading={isSideEffectsLoading}
            sideEffects={sideEffects}
            isOpioidDataLoading={isOpioidDataLoading}
            opioidData={opioidData}
            riskRatingLoading={riskRatingLoading}
            riskRatingData={riskRatingData}
          />

          <NewReportGraph
            billingCycleData={billingCycleData}
            sideEffectQuestions={[]}
            graphs={graphs}
            customHeight="10in"
            customWidth="8in"
          />
          {chunkedQuestions.map((chunk, idx) => {
            const startingIndex = idx === 0 ? 0 : 11 + (idx - 1) * 10;
            return (
              <div key={idx} style={{ pageBreakAfter: "always" }}>
                <NewTransmissionsPage
                  patientId={patientId}
                  customHeight="10in"
                  transmissionData={{ data: chunk }}
                  isLoading={transmissionLoading}
                  error={null}
                  startingIndex={startingIndex}
                />
              </div>
            );
          })}

          {sortedNotes.length && (
            <div style={{ marginBottom: "20px" }}>
              <NewNotesPage
                notes={sortedNotes}
                loading={false}
                isPdf={true}
                customWidth="8in"
                customHeight="10.3in"
                patientBillingMinuteMessage={patientBillingMinuteMessage}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PhysicianReportPage;
